import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "JefesInmediatos";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListaJefes = () => {
  const [jefes, setJefes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloJefes = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloJefes = useMemo(() => getModuloJefes(), []);

  const puedeLeer =
    moduloJefes?.puedeLeer === true ||
    moduloJefes?.leer === true ||
    moduloJefes?.ver === true ||
    moduloJefes?.listar === true ||
    moduloJefes?.read === true ||
    moduloJefes?.view === true;

  const puedeCrear =
    moduloJefes?.puedeCrear === true ||
    moduloJefes?.crear === true ||
    moduloJefes?.create === true;

  const puedeActualizar =
    moduloJefes?.puedeActualizar === true ||
    moduloJefes?.actualizar === true ||
    moduloJefes?.editar === true ||
    moduloJefes?.update === true;

  const puedeEliminar =
    moduloJefes?.puedeEliminar === true ||
    moduloJefes?.eliminar === true ||
    moduloJefes?.delete === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const deleteJefe = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar jefes inmediatos.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(id);

        const { data } = await axios.delete(`/api/jefe/${id}`, authConfig());

        setJefes((prev) => prev.filter((jefe) => jefe._id !== id));

        Swal.fire({
          icon: "success",
          title: data?.message || "Jefe eliminado",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error("Error al eliminar jefe:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el jefe.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setJefes([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get("/api/jefe/listall/", authConfig());

      const lista = Array.isArray(data?.jefes) ? data.jefes : [];

      setJefes([...lista].reverse());
    } catch (error) {
      console.error("Error al cargar los jefes:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudieron cargar los jefes.",
        "error"
      );

      setJefes([]);
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  const dataTabla = useMemo(
    () =>
      jefes.map((jefe) => ({
        ...jefe,

        documentoJefe: (
          <div className="w-100">{jefe.documentoJefe || "N/A"}</div>
        ),

        nombre1Jefe: (
          <div className="w-100">
            <div title={jefe.nombre1Jefe || ""}>
              {jefe.nombre1Jefe || "N/A"}
            </div>
          </div>
        ),

        apellido1Jefe: (
          <div className="w-100">
            <div title={jefe.apellido1Jefe || ""}>
              {jefe.apellido1Jefe || "N/A"}
            </div>
          </div>
        ),

        telefono1Jefe: (
          <div className="w-100">{jefe.telefono1Jefe || "N/A"}</div>
        ),

        emailJefe: (
          <div className="w-100">
            <div
              className="text-truncate"
              style={{ maxWidth: 280 }}
              title={jefe.emailJefe || ""}
            >
              {jefe.emailJefe || "N/A"}
            </div>
          </div>
        ),

        funciones: (
          <div className="d-flex align-items-center justify-content-end gap-2 flex-nowrap">
            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === jefe._id}
                onClick={() =>
                  navigate(`/enteconformador/jefes/edit/${jefe._id}`)
                }
                title="Editar Jefe"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === jefe._id}
                onClick={() => deleteJefe(jefe._id)}
                title="Eliminar Jefe"
                type="button"
              >
                {deletingId === jefe._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      jefes,
      loading,
      deletingId,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteJefe,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "Documento", accessor: "documentoJefe" },
      { Header: "Nombre", accessor: "nombre1Jefe" },
      { Header: "Apellido", accessor: "apellido1Jefe" },
      { Header: "Teléfono", accessor: "telefono1Jefe" },
      {
        Header: "Email",
        accessor: "emailJefe",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <div>
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">Sin permiso de lectura</h5>

              <p className="mb-4">
                No tienes permisos para visualizar los jefes inmediatos.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Jefes Inmediatos / Rectores
                </h5>

                <small className="text-secondary">
                  Gestión de jefes y asociaciones institucionales.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">

                 <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate("/enteconformador/jefes/nuevo")}
                    title="Crear Jefe"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" />
                <div className="mt-3">Cargando jefes…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👤</div>

                <div className="fw-semibold">No hay jefes registrados</div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark mt-3"
                    type="button"
                    onClick={() => navigate("/enteconformador/jefes/nuevo")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar jefe
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaJefes;